import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Company } from '@models/companies/company';
import { Document } from '@models/documents/document';
import { EntitySecurityTransfer } from '@models/security-transactions/entity-security-transfer';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { SecurityType } from '@models/shares/security-type';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { CompanySecurityIssueComponent } from '../../asic-forms/shares/company-security-issue/company-security-issue.component';
import { CertificateConsolidationOptions } from './company-security-issue.model';
import { CompanySecurityTransferComponent } from '../../asic-forms/shares/company-security-transfer/company-security-transfer.component';
import { IStep } from '@models/ui-models/step';
import { BaseShareFormComponent } from "../../asic-forms/shares/base-share-form/base-share-form.component";

export enum CompanySecurityTransferStepsEnum {
  FormDescription = 0,
  TransferFrom = 1,
  TransferTo = 2,
  AuthorisedSignatories = 3,
  Estimate = 4,
}

export const ShareTransferSteps: IStep<CompanySecurityTransferStepsEnum>[] = [
  { step: CompanySecurityTransferStepsEnum.FormDescription, label: 'Form Description' },
  { step: CompanySecurityTransferStepsEnum.TransferFrom, label: 'Transfer From' },
  { step: CompanySecurityTransferStepsEnum.TransferTo, label: 'Transfer To' },
  { step: CompanySecurityTransferStepsEnum.AuthorisedSignatories, label: 'Authorised Signatories' },
  { step: CompanySecurityTransferStepsEnum.Estimate, label: 'Estimate' }
];

export class CompanySecurityTransfer extends EntitySecurityTransfer<Company> {
  static override readonly $type = 'CompanySecurityTransfer';

  consolidateCertificate: boolean;

  constructor(data: Partial<CompanySecurityTransfer> = {}) {
    super(data);

    this.consolidateCertificate = data.consolidateCertificate ?? false;
  }

  override toDictionary(): { key: string; value: string }[] {
    const dict = new ChangeDictionaryHelper();
    const sellers = this.transferFrom.relationshipIds;
    const buyers = this.securityHoldersTo.map(holder => holder.relationshipId);

    dict
      .addIds('Transferor (Seller)', sellers)
      .addWithBeneficialOwnerPrefix('Shares are owned on behalf of', sellers[0])
      .addDivider()
      .addIds('Transferee (Buyer)', buyers)
      .addWithBeneficialOwnerPrefix('Shares are owned on behalf of', this.securityHoldersTo[0].relationshipId)
      .addEmptyLine()
      .add('Share Type', SecurityType.buildIdentifier(this.transferFrom.securityType))
      .add('Number of shares to be transferred', this.transferTo.numberIncrease.toString())
      .addMoney('Consideration paid', this.transferTo.considerationPaid)
      .addMoney('Total Amount Paid', this.transferTo.paidIncrease)
      .addMoney('Total Amount Unpaid', this.transferTo.unpaidIncrease)
      .addIfValueIsNotEmpty('Certificate Number', this.transferTo.shareCertificateNumber)
      .add('Certificate Consolidation', CertificateConsolidationOptions.find(option => option.value === this.consolidateCertificate)?.label)
      .addDate('Date of Transfer', this.transferFrom.transactionDate);

    return dict.dictionary;
  }

  override fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    modalRef = this.fillModalWithCompany(modalRef, actualCompany, true);
    (modalRef.componentInstance as CompanySecurityIssueComponent).companyChangeData.documentId = document.documentId;
    return modalRef;
  }

  override fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit: boolean): NgbModalRef {
    const instance = modalRef.componentInstance as CompanySecurityTransferComponent;
    instance.isEdit = isEdit;
    instance.companyChangeData = new CompanyChangeData(company);
    instance.formModel = this;
    instance.hasShareIssue = BaseShareFormComponent.isCompanyIncludeShareIssue(company);

    return modalRef;
  }

  override prepareToRequest(): CompanySecurityTransfer {
    return {
      ...this,
      ...super.prepareToRequest(),
      transferFrom: this.transferFrom.prepareToRequest(),
      transferTo: this.transferTo.prepareToRequest(),
    };
  }
}
